import { useState, useRef } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Download,
  Home,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useAutoAddRules, useOrgAutoAddRules } from '../../hooks/useAutoAddRules';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ConfirmModal } from './ConfirmModal';
import { CustomDropdown } from '../ui/CustomDropdown';
import type {
  MasterResidentialRoom,
  OrgResidentialRoom,
  MasterRoomAutoAddConfig,
  OrgRoomAutoAddConfig,
  RoomAutoAddRule,
  AutoAddCondition,
  AutoAddRuleField,
  AutoAddNumericOp,
  AutoAddEnumOp,
  AutoAddLogic,
  AutoAddQuantity,
} from '../../types/admin';

// ── Field metadata ──────────────────────────────────────────────────────────

interface FieldMeta {
  key: AutoAddRuleField;
  label: string;
  type: 'numeric' | 'boolean' | 'enum';
  unit?: string;
  enumValues?: { value: string; label: string }[];
}

const PROPERTY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'manufactured', label: 'Manufactured' },
  { value: 'multi_family', label: 'Multi-Family' },
];

const FIELDS: FieldMeta[] = [
  { key: 'propertyType', label: 'Property Type', type: 'enum', enumValues: PROPERTY_TYPE_OPTIONS },
  { key: 'bedrooms', label: 'Bedrooms', type: 'numeric' },
  { key: 'bathrooms', label: 'Bathrooms', type: 'numeric' },
  { key: 'totalRoomCount', label: 'Total Rooms', type: 'numeric' },
  { key: 'squareFootage', label: 'Square Footage', type: 'numeric', unit: 'sq ft' },
  { key: 'lotSize', label: 'Lot Size', type: 'numeric', unit: 'sq ft' },
  { key: 'garageSpaces', label: 'Parking Spaces', type: 'numeric' },
  { key: 'basement', label: 'Basement', type: 'boolean' },
  { key: 'pool', label: 'Pool', type: 'boolean' },
];

const DRIVEN_BY_OPTIONS: { value: string; label: string }[] = [
  { value: 'bedrooms', label: 'Bedrooms' },
  { value: 'bathrooms', label: 'Bathrooms' },
  { value: 'totalRoomCount', label: 'Total Rooms' },
  { value: 'garageSpaces', label: 'Parking Spaces' },
];

const NUMERIC_OPS: { value: AutoAddNumericOp; label: string }[] = [
  { value: '>=', label: 'is at least' },
  { value: '<=', label: 'is at most' },
  { value: '>', label: 'is more than' },
  { value: '<', label: 'is less than' },
  { value: '==', label: 'equals' },
  { value: '!=', label: 'does not equal' },
];

function getFieldMeta(key: AutoAddRuleField): FieldMeta {
  return FIELDS.find(f => f.key === key) ?? FIELDS[0];
}

// ── Default rule ────────────────────────────────────────────────────────────

function defaultRule(): RoomAutoAddRule {
  return {
    trigger: 'always',
    conditions: [],
    logic: 'AND',
    quantity: { mode: 'fixed', fixed: 1 },
  };
}

function defaultCondition(): AutoAddCondition {
  return { field: 'squareFootage', operator: '>=', value: 1000 };
}

// ── Summary helpers ─────────────────────────────────────────────────────────

function summarizeCondition(c: AutoAddCondition): string {
  const meta = getFieldMeta(c.field);
  if (meta.type === 'boolean') {
    return `${meta.label} is ${c.value ? 'Yes' : 'No'}`;
  }
  if (meta.type === 'enum' && Array.isArray(c.value)) {
    const labels = (c.value as string[]).map(v =>
      meta.enumValues?.find(e => e.value === v)?.label ?? v
    );
    return c.operator === 'in'
      ? `${meta.label} is ${labels.join(', ')}`
      : `${meta.label} is not ${labels.join(', ')}`;
  }
  const opLabel = NUMERIC_OPS.find(o => o.value === c.operator)?.label ?? c.operator;
  const unit = meta.unit ? ` ${meta.unit}` : '';
  return `${meta.label} ${opLabel} ${c.value}${unit}`;
}

function summarizeQuantity(q: AutoAddQuantity): string {
  if (q.mode === 'fixed') return `Qty: ${q.fixed ?? 1}`;
  const driven = DRIVEN_BY_OPTIONS.find(d => d.value === q.drivenBy)?.label ?? q.drivenBy;
  let s = `Qty: driven by ${driven}`;
  if (q.offset && q.offset !== 0) s += ` ${q.offset > 0 ? '+' : ''}${q.offset}`;
  if (q.min != null) s += `, min ${q.min}`;
  if (q.max != null) s += `, max ${q.max}`;
  return s;
}

function summarizeRule(rule: RoomAutoAddRule): string {
  const qty = summarizeQuantity(rule.quantity);
  if (rule.trigger === 'always') return `Always add. ${qty}.`;
  const joiner = rule.logic === 'OR' ? ' OR ' : ' AND ';
  const conds = rule.conditions.map(summarizeCondition).join(joiner);
  return `Add when ${conds}. ${qty}.`;
}

function summarizeRules(rules: RoomAutoAddRule[]): string {
  if (rules.length === 0) return 'No rules — room will not be auto-added';
  if (rules.length === 1) return summarizeRule(rules[0]);
  return `${rules.length} rules configured`;
}

// ── Shared UI primitives ────────────────────────────────────────────────────

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 px-3 rounded-lg border text-[12px] font-semibold transition-all duration-150
        ${active
          ? 'border-plum bg-plum/5 text-plum'
          : 'border-stone bg-white text-midnight hover:border-plum/40'
        }`}
    >
      {children}
    </button>
  );
}

function StyledSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[10px] font-bold uppercase tracking-widest text-slate">
        {label}
      </label>
      <CustomDropdown
        id={id}
        value={value}
        options={options}
        onChange={onChange}
        fullWidth={false}
      />
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  placeholder,
  min,
  step,
  helpText,
}: {
  id: string;
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
  min?: number;
  step?: number;
  helpText?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[10px] font-bold uppercase tracking-widest text-slate">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={min}
        step={step ?? 1}
        value={value ?? ''}
        onChange={e => {
          const v = e.target.value;
          onChange(v === '' ? undefined : Number(v));
        }}
        placeholder={placeholder}
        className="h-11 w-20 px-3 text-center rounded-lg border border-stone bg-white text-small text-midnight
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors"
      />
      {helpText && (
        <p className="text-[10px] text-slate leading-snug">{helpText}</p>
      )}
    </div>
  );
}

// ── Quantity Editor (shared across all 3 options) ───────────────────────────

function QuantityEditor({
  quantity,
  onChange,
  idPrefix,
}: {
  quantity: AutoAddQuantity;
  onChange: (q: AutoAddQuantity) => void;
  idPrefix: string;
}) {
  const drivenByLabel = DRIVEN_BY_OPTIONS.find(d => d.value === quantity.drivenBy)?.label ?? 'field';

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Quantity</p>
      <div className="flex gap-1.5 flex-wrap">
        <Pill active={quantity.mode === 'fixed'} onClick={() => onChange({ ...quantity, mode: 'fixed', fixed: quantity.fixed ?? 1 })}>
          Fixed
        </Pill>
        <Pill active={quantity.mode === 'driven'} onClick={() => onChange({ ...quantity, mode: 'driven', drivenBy: quantity.drivenBy ?? 'bedrooms' })}>
          Driven by field
        </Pill>
      </div>

      {quantity.mode === 'fixed' && (
        <NumberField
          id={`${idPrefix}-qty-fixed`}
          label="Count"
          value={quantity.fixed}
          onChange={v => onChange({ ...quantity, fixed: v ?? 1 })}
          min={0}
          placeholder="1"
        />
      )}

      {quantity.mode === 'driven' && (
        <div className="flex flex-wrap gap-3 items-start">
          <StyledSelect
            id={`${idPrefix}-qty-driven`}
            label="Driven By"
            value={quantity.drivenBy ?? 'bedrooms'}
            options={DRIVEN_BY_OPTIONS}
            onChange={v => onChange({ ...quantity, drivenBy: v as AutoAddQuantity['drivenBy'] })}
          />
          <NumberField
            id={`${idPrefix}-qty-offset`}
            label="Adjust By"
            value={quantity.offset}
            onChange={v => onChange({ ...quantity, offset: v })}
            placeholder="0"
            helpText={`e.g. −1 = ${drivenByLabel} minus 1`}
          />
          <NumberField
            id={`${idPrefix}-qty-min`}
            label="No Fewer Than"
            value={quantity.min}
            onChange={v => onChange({ ...quantity, min: v })}
            placeholder="0"
            min={0}
            helpText="Floor for result"
          />
          <NumberField
            id={`${idPrefix}-qty-max`}
            label="No More Than"
            value={quantity.max}
            onChange={v => onChange({ ...quantity, max: v })}
            placeholder="10"
            min={0}
            helpText="Cap for result"
          />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// OPTION A — Simple Preset Rule Builder
// ══════════════════════════════════════════════════════════════════════════════

type SimpleTemplate = 'always' | 'field_threshold' | 'property_type_match' | 'boolean_check';

const SIMPLE_TEMPLATES: { value: SimpleTemplate; label: string; description: string }[] = [
  { value: 'always', label: 'Always Add', description: 'Room is always included in the estimate' },
  { value: 'field_threshold', label: 'Number Threshold', description: 'Add when a numeric field meets a threshold' },
  { value: 'property_type_match', label: 'Property Type', description: 'Add for specific property types' },
  { value: 'boolean_check', label: 'Feature Check', description: 'Add when a feature is present (e.g. pool, basement)' },
];

function ruleToSimpleTemplate(rule: RoomAutoAddRule): SimpleTemplate {
  if (rule.trigger === 'always') return 'always';
  if (rule.conditions.length === 0) return 'always';
  const first = rule.conditions[0];
  if (first.field === 'propertyType') return 'property_type_match';
  if (getFieldMeta(first.field).type === 'boolean') return 'boolean_check';
  return 'field_threshold';
}

function OptionAEditor({
  rule,
  onChange,
  idPrefix,
}: {
  rule: RoomAutoAddRule;
  onChange: (r: RoomAutoAddRule) => void;
  idPrefix: string;
}) {
  const template = ruleToSimpleTemplate(rule);

  function setTemplate(t: SimpleTemplate) {
    if (t === 'always') {
      onChange({ ...rule, trigger: 'always', conditions: [] });
    } else if (t === 'field_threshold') {
      onChange({
        ...rule,
        trigger: 'conditional',
        conditions: [{ field: 'squareFootage', operator: '>=', value: 1000 }],
      });
    } else if (t === 'property_type_match') {
      onChange({
        ...rule,
        trigger: 'conditional',
        conditions: [{ field: 'propertyType', operator: 'in', value: ['single_family'] }],
      });
    } else if (t === 'boolean_check') {
      onChange({
        ...rule,
        trigger: 'conditional',
        conditions: [{ field: 'basement', operator: '==', value: true }],
      });
    }
  }

  const cond = rule.conditions[0] ?? null;

  return (
    <div className="flex flex-col gap-4">
      {/* Template selector */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate">Rule Type</p>
        <div className="grid grid-cols-2 gap-2">
          {SIMPLE_TEMPLATES.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTemplate(t.value)}
              className={`text-left px-3 py-2.5 rounded-xl border transition-all duration-150
                ${template === t.value
                  ? 'border-plum bg-plum/5'
                  : 'border-stone bg-white hover:border-plum/40'
                }`}
            >
              <p className={`text-[12px] font-semibold ${template === t.value ? 'text-plum' : 'text-midnight'}`}>
                {t.label}
              </p>
              <p className="text-[11px] text-slate leading-snug mt-0.5">{t.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Template-specific fields */}
      {template === 'field_threshold' && cond && (
        <div className="flex flex-wrap items-end gap-3 bg-mist rounded-xl px-3 py-3">
          <StyledSelect
            id={`${idPrefix}-field`}
            label="When"
            value={cond.field}
            options={FIELDS.filter(f => f.type === 'numeric').map(f => ({ value: f.key, label: f.label }))}
            onChange={v => onChange({
              ...rule,
              conditions: [{ ...cond, field: v as AutoAddRuleField }],
            })}
          />
          <StyledSelect
            id={`${idPrefix}-op`}
            label="Is"
            value={cond.operator}
            options={NUMERIC_OPS}
            onChange={v => onChange({
              ...rule,
              conditions: [{ ...cond, operator: v as AutoAddNumericOp }],
            })}
          />
          <NumberField
            id={`${idPrefix}-val`}
            label="Value"
            value={cond.value as number}
            onChange={v => onChange({
              ...rule,
              conditions: [{ ...cond, value: v ?? 0 }],
            })}
            placeholder="0"
          />
        </div>
      )}

      {template === 'property_type_match' && cond && (
        <div className="flex flex-col gap-2 bg-mist rounded-xl px-3 py-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate">
            Add for these property types
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PROPERTY_TYPE_OPTIONS.map(pt => {
              const selected = Array.isArray(cond.value) && (cond.value as string[]).includes(pt.value);
              return (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => {
                    const current = Array.isArray(cond.value) ? (cond.value as string[]) : [];
                    const next = selected ? current.filter(v => v !== pt.value) : [...current, pt.value];
                    onChange({
                      ...rule,
                      conditions: [{ ...cond, value: next.length > 0 ? next : [pt.value] }],
                    });
                  }}
                  className={`h-8 px-3 rounded-lg border text-[11px] font-semibold transition-all duration-150
                    ${selected
                      ? 'border-plum bg-plum/10 text-plum'
                      : 'border-stone bg-white text-midnight hover:border-plum/40'
                    }`}
                >
                  {pt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {template === 'boolean_check' && cond && (
        <div className="flex items-end gap-3 bg-mist rounded-xl px-3 py-3">
          <StyledSelect
            id={`${idPrefix}-bool-field`}
            label="Add when"
            value={cond.field}
            options={FIELDS.filter(f => f.type === 'boolean').map(f => ({ value: f.key, label: f.label }))}
            onChange={v => onChange({
              ...rule,
              conditions: [{ field: v as AutoAddRuleField, operator: '==', value: true }],
            })}
          />
          <p className="text-small text-midnight pb-3 font-medium">is present</p>
        </div>
      )}

      {/* Quantity */}
      <QuantityEditor quantity={rule.quantity} onChange={q => onChange({ ...rule, quantity: q })} idPrefix={idPrefix} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// OPTION B — Visual Condition Cards
// ══════════════════════════════════════════════════════════════════════════════

function ConditionRow({
  condition,
  onChange,
  onRemove,
  idPrefix,
}: {
  condition: AutoAddCondition;
  onChange: (c: AutoAddCondition) => void;
  onRemove: () => void;
  idPrefix: string;
}) {
  const meta = getFieldMeta(condition.field);

  function handleFieldChange(fieldKey: string) {
    const newMeta = getFieldMeta(fieldKey as AutoAddRuleField);
    let newOp: string = condition.operator;
    let newValue: number | boolean | string[] = condition.value;
    if (newMeta.type === 'numeric') {
      newOp = '>=';
      newValue = typeof condition.value === 'number' ? condition.value : 0;
    } else if (newMeta.type === 'boolean') {
      newOp = '==';
      newValue = true;
    } else if (newMeta.type === 'enum') {
      newOp = 'in';
      newValue = [];
    }
    onChange({ field: fieldKey as AutoAddRuleField, operator: newOp as AutoAddCondition['operator'], value: newValue });
  }

  return (
    <div className="flex items-start gap-2 bg-mist rounded-xl px-3 py-2.5">
      <div className="flex-1 flex flex-wrap items-end gap-2">
        <StyledSelect
          id={`${idPrefix}-field`}
          label="Field"
          value={condition.field}
          options={FIELDS.map(f => ({ value: f.key, label: f.label }))}
          onChange={handleFieldChange}
        />

        {meta.type === 'numeric' && (
          <>
            <StyledSelect
              id={`${idPrefix}-op`}
              label="Operator"
              value={condition.operator}
              options={NUMERIC_OPS}
              onChange={v => onChange({ ...condition, operator: v as AutoAddNumericOp })}
            />
            <NumberField
              id={`${idPrefix}-val`}
              label="Value"
              value={condition.value as number}
              onChange={v => onChange({ ...condition, value: v ?? 0 })}
              placeholder="0"
            />
          </>
        )}

        {meta.type === 'boolean' && (
          <div className="flex gap-1.5 pb-0.5">
            <Pill active={condition.value === true} onClick={() => onChange({ ...condition, value: true, operator: '==' })}>
              Yes
            </Pill>
            <Pill active={condition.value === false} onClick={() => onChange({ ...condition, value: false, operator: '==' })}>
              No
            </Pill>
          </div>
        )}

        {meta.type === 'enum' && (
          <div className="flex flex-wrap gap-1.5 pb-0.5">
            {meta.enumValues?.map(ev => {
              const vals = Array.isArray(condition.value) ? condition.value as string[] : [];
              const sel = vals.includes(ev.value);
              return (
                <button
                  key={ev.value}
                  type="button"
                  onClick={() => {
                    const next = sel ? vals.filter(v => v !== ev.value) : [...vals, ev.value];
                    onChange({ ...condition, value: next, operator: 'in' as AutoAddEnumOp });
                  }}
                  className={`h-8 px-2.5 rounded-lg border text-[11px] font-semibold transition-all
                    ${sel ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-midnight hover:border-plum/40'}`}
                >
                  {ev.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 mt-5 w-7 h-7 flex items-center justify-center rounded-lg
                   text-slate hover:text-rose hover:bg-rose/10 transition-colors"
        aria-label="Remove condition"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function OptionBEditor({
  rule,
  onChange,
  idPrefix,
}: {
  rule: RoomAutoAddRule;
  onChange: (r: RoomAutoAddRule) => void;
  idPrefix: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* Trigger */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate">When to Add</p>
        <div className="flex gap-1.5">
          <Pill active={rule.trigger === 'always'} onClick={() => onChange({ ...rule, trigger: 'always', conditions: [] })}>
            Always
          </Pill>
          <Pill
            active={rule.trigger === 'conditional'}
            onClick={() => onChange({
              ...rule,
              trigger: 'conditional',
              conditions: rule.conditions.length > 0 ? rule.conditions : [defaultCondition()],
            })}
          >
            When conditions are met
          </Pill>
        </div>
      </div>

      {/* Conditions */}
      {rule.trigger === 'conditional' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate flex-1">Conditions</p>
            {rule.conditions.length > 1 && (
              <div className="flex gap-1">
                <Pill active={rule.logic === 'AND'} onClick={() => onChange({ ...rule, logic: 'AND' })}>
                  ALL must match
                </Pill>
                <Pill active={rule.logic === 'OR'} onClick={() => onChange({ ...rule, logic: 'OR' })}>
                  ANY can match
                </Pill>
              </div>
            )}
          </div>

          {rule.conditions.map((cond, i) => (
            <div key={i}>
              {i > 0 && (
                <div className="flex items-center gap-2 py-1 px-3">
                  <div className="flex-1 h-px bg-stone" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate">
                    {rule.logic}
                  </span>
                  <div className="flex-1 h-px bg-stone" />
                </div>
              )}
              <ConditionRow
                condition={cond}
                onChange={updated => {
                  const next = [...rule.conditions];
                  next[i] = updated;
                  onChange({ ...rule, conditions: next });
                }}
                onRemove={() => {
                  const next = rule.conditions.filter((_, j) => j !== i);
                  onChange({
                    ...rule,
                    conditions: next,
                    trigger: next.length === 0 ? 'always' : 'conditional',
                  });
                }}
                idPrefix={`${idPrefix}-c${i}`}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={() => onChange({ ...rule, conditions: [...rule.conditions, defaultCondition()] })}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:underline self-start pl-1"
          >
            <Plus size={13} /> Add condition
          </button>
        </div>
      )}

      {/* Quantity */}
      <QuantityEditor quantity={rule.quantity} onChange={q => onChange({ ...rule, quantity: q })} idPrefix={idPrefix} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// OPTION C — Sentence-Style Natural Language Builder
// ══════════════════════════════════════════════════════════════════════════════

function SentenceCondition({
  condition,
  onChange,
  onRemove,
  showJoiner,
  logic,
  onToggleLogic,
  idPrefix,
}: {
  condition: AutoAddCondition;
  onChange: (c: AutoAddCondition) => void;
  onRemove: () => void;
  showJoiner: boolean;
  logic: AutoAddLogic;
  onToggleLogic: () => void;
  idPrefix: string;
}) {
  const meta = getFieldMeta(condition.field);

  return (
    <div className="flex flex-col gap-1">
      {showJoiner && (
        <button
          type="button"
          onClick={onToggleLogic}
          className="self-start ml-2 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider
                     text-plum bg-plum/10 hover:bg-plum/20 transition-colors"
        >
          {logic}
        </button>
      )}
      <div className="flex items-center gap-1.5 flex-wrap bg-mist rounded-xl px-3 py-2.5">
        {/* "when" */}
        <span className="text-small text-slate font-medium">when</span>

        {/* Field picker */}
        <div className="min-w-[140px]">
          <CustomDropdown
            id={`${idPrefix}-field`}
            value={condition.field}
            options={FIELDS.map(f => ({ value: f.key, label: f.label }))}
            onChange={v => {
              const f = v as AutoAddRuleField;
              const m = getFieldMeta(f);
              if (m.type === 'numeric') onChange({ field: f, operator: '>=', value: typeof condition.value === 'number' ? condition.value : 0 });
              else if (m.type === 'boolean') onChange({ field: f, operator: '==', value: true });
              else onChange({ field: f, operator: 'in', value: [] });
            }}
            fullWidth={false}
          />
        </div>

        {/* Verb / operator */}
        {meta.type === 'numeric' && (
          <>
            <div className="min-w-[130px]">
              <CustomDropdown
                value={condition.operator}
                options={NUMERIC_OPS}
                onChange={v => onChange({ ...condition, operator: v as AutoAddNumericOp })}
                fullWidth={false}
              />
            </div>
            <input
              type="number"
              value={typeof condition.value === 'number' ? condition.value : ''}
              onChange={e => onChange({ ...condition, value: Number(e.target.value) || 0 })}
              className="h-11 w-20 px-3 text-center rounded-lg border border-stone bg-white text-small text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
            />
            {meta.unit && <span className="text-[11px] text-slate">{meta.unit}</span>}
          </>
        )}

        {meta.type === 'boolean' && (
          <>
            <span className="text-small text-midnight font-medium">is</span>
            <div className="flex gap-1">
              <Pill active={condition.value === true} onClick={() => onChange({ ...condition, value: true })}>present</Pill>
              <Pill active={condition.value === false} onClick={() => onChange({ ...condition, value: false })}>absent</Pill>
            </div>
          </>
        )}

        {meta.type === 'enum' && (
          <>
            <span className="text-small text-midnight font-medium">is</span>
            <div className="flex flex-wrap gap-1">
              {meta.enumValues?.map(ev => {
                const vals = Array.isArray(condition.value) ? condition.value as string[] : [];
                const sel = vals.includes(ev.value);
                return (
                  <button
                    key={ev.value}
                    type="button"
                    onClick={() => {
                      const next = sel ? vals.filter(v => v !== ev.value) : [...vals, ev.value];
                      onChange({ ...condition, value: next, operator: 'in' });
                    }}
                    className={`h-7 px-2 rounded-md border text-[11px] font-semibold transition-all
                      ${sel ? 'border-plum bg-plum/10 text-plum' : 'border-stone bg-white text-midnight hover:border-plum/40'}`}
                  >
                    {ev.label}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-lg
                     text-slate hover:text-rose hover:bg-rose/10 transition-colors ml-auto"
          aria-label="Remove"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function SentenceQuantity({
  quantity,
  onChange,
  idPrefix,
}: {
  quantity: AutoAddQuantity;
  onChange: (q: AutoAddQuantity) => void;
  idPrefix: string;
}) {
  const drivenByLabel = DRIVEN_BY_OPTIONS.find(d => d.value === quantity.drivenBy)?.label ?? 'field';

  return (
    <div className="flex items-center gap-1.5 flex-wrap bg-mist rounded-xl px-3 py-2.5">
      <span className="text-small text-slate font-medium">add</span>
      <div className="flex gap-1">
        <Pill active={quantity.mode === 'fixed'} onClick={() => onChange({ ...quantity, mode: 'fixed', fixed: quantity.fixed ?? 1 })}>
          exactly
        </Pill>
        <Pill active={quantity.mode === 'driven'} onClick={() => onChange({ ...quantity, mode: 'driven', drivenBy: quantity.drivenBy ?? 'bedrooms' })}>
          based on
        </Pill>
      </div>

      {quantity.mode === 'fixed' && (
        <>
          <input
            type="number"
            min={0}
            value={quantity.fixed ?? 1}
            onChange={e => onChange({ ...quantity, fixed: Number(e.target.value) || 1 })}
            className="h-11 w-16 px-2 text-center rounded-lg border border-stone bg-white text-small font-semibold text-midnight
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors"
          />
          <span className="text-small text-midnight font-medium">room(s)</span>
        </>
      )}

      {quantity.mode === 'driven' && (
        <>
          <div className="min-w-[130px]">
            <CustomDropdown
              id={`${idPrefix}-sq-driven`}
              value={quantity.drivenBy ?? 'bedrooms'}
              options={DRIVEN_BY_OPTIONS}
              onChange={v => onChange({ ...quantity, drivenBy: v as AutoAddQuantity['drivenBy'] })}
              fullWidth={false}
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate whitespace-nowrap">adjust by</span>
            <input
              type="number"
              value={quantity.offset ?? 0}
              onChange={e => onChange({ ...quantity, offset: Number(e.target.value) })}
              title={`e.g. −1 means ${drivenByLabel} minus 1`}
              className="h-11 w-14 px-1 text-center rounded-lg border border-stone bg-white text-small text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate whitespace-nowrap">no fewer than</span>
            <input
              type="number"
              min={0}
              value={quantity.min ?? 0}
              onChange={e => onChange({ ...quantity, min: Number(e.target.value) })}
              className="h-11 w-14 px-1 text-center rounded-lg border border-stone bg-white text-small text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate whitespace-nowrap">no more than</span>
            <input
              type="number"
              min={0}
              value={quantity.max ?? 10}
              onChange={e => onChange({ ...quantity, max: Number(e.target.value) })}
              className="h-11 w-14 px-1 text-center rounded-lg border border-stone bg-white text-small text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
            />
          </div>
        </>
      )}
    </div>
  );
}

function OptionCEditor({
  rule,
  onChange,
  idPrefix,
}: {
  rule: RoomAutoAddRule;
  onChange: (r: RoomAutoAddRule) => void;
  idPrefix: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* Trigger sentence */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-small text-midnight font-medium">This room should be added</span>
        <div className="flex gap-1">
          <Pill active={rule.trigger === 'always'} onClick={() => onChange({ ...rule, trigger: 'always', conditions: [] })}>
            always
          </Pill>
          <Pill
            active={rule.trigger === 'conditional'}
            onClick={() => onChange({
              ...rule,
              trigger: 'conditional',
              conditions: rule.conditions.length > 0 ? rule.conditions : [defaultCondition()],
            })}
          >
            when...
          </Pill>
        </div>
      </div>

      {/* Condition sentences */}
      {rule.trigger === 'conditional' && (
        <div className="flex flex-col gap-1 pl-2 border-l-2 border-plum/20">
          {rule.conditions.map((cond, i) => (
            <SentenceCondition
              key={i}
              condition={cond}
              onChange={updated => {
                const next = [...rule.conditions];
                next[i] = updated;
                onChange({ ...rule, conditions: next });
              }}
              onRemove={() => {
                const next = rule.conditions.filter((_, j) => j !== i);
                onChange({ ...rule, conditions: next, trigger: next.length === 0 ? 'always' : 'conditional' });
              }}
              showJoiner={i > 0}
              logic={rule.logic}
              onToggleLogic={() => onChange({ ...rule, logic: rule.logic === 'AND' ? 'OR' : 'AND' })}
              idPrefix={`${idPrefix}-c${i}`}
            />
          ))}
          <button
            type="button"
            onClick={() => onChange({ ...rule, conditions: [...rule.conditions, defaultCondition()] })}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:underline self-start pl-3 mt-1"
          >
            <Plus size={13} /> and another condition...
          </button>
        </div>
      )}

      {/* Quantity sentence */}
      <SentenceQuantity
        quantity={rule.quantity}
        onChange={q => onChange({ ...rule, quantity: q })}
        idPrefix={idPrefix}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Rule editor wrapper — renders one rule with a specific UI option
// ══════════════════════════════════════════════════════════════════════════════

function SingleRuleEditor({
  rule,
  onChange,
  uiOption,
  idPrefix,
}: {
  rule: RoomAutoAddRule;
  onChange: (r: RoomAutoAddRule) => void;
  uiOption: UIOption;
  idPrefix: string;
}) {
  if (uiOption === 'A') return <OptionAEditor rule={rule} onChange={onChange} idPrefix={idPrefix} />;
  if (uiOption === 'B') return <OptionBEditor rule={rule} onChange={onChange} idPrefix={idPrefix} />;
  return <OptionCEditor rule={rule} onChange={onChange} idPrefix={idPrefix} />;
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Auto-Add Rules Tab
// ══════════════════════════════════════════════════════════════════════════════

type UIOption = 'A' | 'B' | 'C';

const UI_OPTIONS: { key: UIOption; label: string; description: string }[] = [
  { key: 'A', label: 'Option A — Simple Presets', description: 'Pick a rule type from 4 templates. Easiest to use, covers most common rules.' },
  { key: 'B', label: 'Option B — Condition Cards', description: 'Build conditions with AND/OR logic. More flexible, structured approach.' },
  { key: 'C', label: 'Option C — Sentence Builder', description: 'Reads like English: "Add when bedrooms is at least 3." Natural and intuitive.' },
];

// ── Shared room card list (multi-rule) ───────────────────────────────────────

function RoomCardList({
  configs,
  editing,
  uiOption,
  onUpdateRules,
}: {
  configs: MasterRoomAutoAddConfig[];
  editing: boolean;
  uiOption: UIOption;
  onUpdateRules: (roomId: string, rules: RoomAutoAddRule[]) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpanded(roomId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-2">
      {configs.map(config => {
        const isOpen = expanded.has(config.roomId);
        const rules = config.rules ?? [];
        return (
          <div
            key={config.roomId}
            className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggleExpanded(config.roomId)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left
                         hover:bg-mist active:bg-stone transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-mist flex items-center justify-center shrink-0">
                <Home size={14} className="text-slate" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-small font-semibold text-midnight truncate">{config.roomName}</p>
                <p className="text-[11px] text-slate mt-0.5 leading-snug truncate">
                  {summarizeRules(rules)}
                </p>
              </div>
              {rules.length > 0 && (
                <span className="shrink-0 h-5 min-w-[20px] px-1 rounded-full bg-plum/10 text-plum text-[10px] font-bold flex items-center justify-center">
                  {rules.length}
                </span>
              )}
              <ChevronDown
                size={16}
                className={`text-slate shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isOpen && (
              <div className="px-4 pb-4 pt-1 border-t border-stone">
                {editing ? (
                  <div className="flex flex-col gap-3">
                    {rules.length === 0 && (
                      <p className="text-[12px] text-slate py-2 italic">
                        No rules yet — this room will not be auto-added. Click below to add a rule.
                      </p>
                    )}

                    {rules.map((rule, ruleIdx) => (
                      <div key={ruleIdx} className="relative">
                        {/* Rule header with delete */}
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-slate flex-1">
                            Rule {ruleIdx + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const next = rules.filter((_, j) => j !== ruleIdx);
                              onUpdateRules(config.roomId, next);
                            }}
                            className="flex items-center gap-1 text-[11px] font-semibold text-slate
                                       hover:text-rose transition-colors"
                          >
                            <Trash2 size={11} /> Remove
                          </button>
                        </div>
                        <div className="border border-stone rounded-xl p-3">
                          <SingleRuleEditor
                            rule={rule}
                            onChange={updated => {
                              const next = [...rules];
                              next[ruleIdx] = updated;
                              onUpdateRules(config.roomId, next);
                            }}
                            uiOption={uiOption}
                            idPrefix={`${config.roomId}-r${ruleIdx}`}
                          />
                        </div>
                        {ruleIdx < rules.length - 1 && (
                          <div className="flex items-center gap-2 py-2 px-3">
                            <div className="flex-1 h-px bg-stone" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-plum/60">
                              OR
                            </span>
                            <div className="flex-1 h-px bg-stone" />
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        onUpdateRules(config.roomId, [...rules, defaultRule()]);
                      }}
                      className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:underline self-start"
                    >
                      <Plus size={13} /> Add rule
                    </button>
                  </div>
                ) : (
                  /* View mode */
                  <div className="py-2">
                    {rules.length === 0 ? (
                      <p className="text-small text-slate italic">No rules — room will not be auto-added</p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {rules.map((rule, i) => (
                          <p key={i} className="text-small text-midnight">
                            {rules.length > 1 && <span className="text-slate font-semibold mr-1">Rule {i + 1}:</span>}
                            {summarizeRule(rule)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Merge helper ────────────────────────────────────────────────────────────

function mergeRoomsAndRules(
  rooms: { id: string; name: string }[],
  configs: MasterRoomAutoAddConfig[],
): MasterRoomAutoAddConfig[] {
  const configMap = new Map(configs.map(c => [c.roomId, c]));
  return rooms.map(room => {
    const existing = configMap.get(room.id);
    if (!existing) return { roomId: room.id, roomName: room.name, rules: [] };
    // Normalize: legacy data may have singular `rule` instead of `rules` array
    const legacy = existing as MasterRoomAutoAddConfig & { rule?: RoomAutoAddRule };
    const rules = Array.isArray(existing.rules)
      ? existing.rules
      : legacy.rule ? [legacy.rule] : [];
    return { ...existing, rules };
  });
}

// ══════════════════════════════════════════════════════════════════════════════
// Master Auto-Add Rules Tab (Platform Admin)
// ══════════════════════════════════════════════════════════════════════════════

export function AutoAddRulesTab({ rooms }: { rooms: MasterResidentialRoom[] }) {
  const { rules: configs, loading, error, saveRules, bulkUpload } = useAutoAddRules();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MasterRoomAutoAddConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uiOption, setUiOption] = useState<UIOption>('A');

  // Bulk upload state
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ created: number; updated: number } | null>(null);
  const [bulkError, setBulkError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const merged = mergeRoomsAndRules(rooms, editing ? draft : configs);

  function startEditing() {
    setDraft(mergeRoomsAndRules(rooms, configs));
    setEditing(true);
    setSaveError('');
    setSaveSuccess(false);
  }

  function cancelEditing() {
    setEditing(false);
    setDraft([]);
    setSaveError('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      // Only save rooms that have rules
      const toSave = draft.filter(d => d.rules.length > 0);
      await saveRules(toSave);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function updateRoomRules(roomId: string, rules: RoomAutoAddRule[]) {
    setDraft(prev => prev.map(r => r.roomId === roomId ? { ...r, rules } : r));
  }

  // ── Bulk download / upload ──────────────────────────────────────────────

  function downloadExistingData() {
    const exportData = merged
      .filter(cfg => cfg.rules.length > 0)
      .map(cfg => ({
        roomName: cfg.roomName,
        rules: cfg.rules,
      }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'master-auto-add-rules.json';
    a.click();
    URL.revokeObjectURL(url);
    setBulkMenuOpen(false);
  }

  function downloadTemplate() {
    const template = [
      {
        roomName: 'Kitchen',
        rules: [{
          trigger: 'always',
          conditions: [],
          logic: 'AND',
          quantity: { mode: 'fixed', fixed: 1 },
        }],
      },
      {
        roomName: 'Bedroom (Guest/Secondary)',
        rules: [{
          trigger: 'conditional',
          conditions: [{ field: 'bedrooms', operator: '>=', value: 2 }],
          logic: 'AND',
          quantity: { mode: 'driven', drivenBy: 'bedrooms', offset: -1, min: 0, max: 8 },
        }],
      },
      {
        roomName: 'Garage',
        rules: [
          {
            trigger: 'conditional',
            conditions: [{ field: 'propertyType', operator: 'in', value: ['single_family', 'townhouse'] }],
            logic: 'AND',
            quantity: { mode: 'fixed', fixed: 1 },
          },
          {
            trigger: 'conditional',
            conditions: [{ field: 'garageSpaces', operator: '>=', value: 1 }],
            logic: 'AND',
            quantity: { mode: 'driven', drivenBy: 'garageSpaces', offset: 0, min: 1, max: 3 },
          },
        ],
      },
    ];
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'auto-add-rules-template.json';
    a.click();
    URL.revokeObjectURL(url);
    setBulkMenuOpen(false);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkMenuOpen(false);
    setBulkUploading(true);
    setBulkError('');
    setBulkResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        throw new Error('JSON file must contain an array of room rule configs.');
      }
      const result = await bulkUpload(parsed);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to upload rules.');
    } finally {
      setBulkUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 gap-3 text-center">
        <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
          <Home size={24} className="text-slate" />
        </div>
        <p className="text-small text-slate">Add rooms in the Room Inventories tab first.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* UI Option Switcher — for reviewing all 3 designs */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <p className="text-[12px] font-semibold text-amber-800 mb-2">
          Design Review: Choose a UI style to preview
        </p>
        <div className="flex flex-col gap-1.5">
          {UI_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setUiOption(opt.key)}
              className={`text-left px-3 py-2 rounded-lg border transition-all text-[12px]
                ${uiOption === opt.key
                  ? 'border-amber-400 bg-amber-100 text-amber-900 font-semibold'
                  : 'border-amber-200 bg-white text-amber-800 hover:border-amber-300'
                }`}
            >
              <span className="font-semibold">{opt.label}</span>
              <span className="text-amber-600 ml-1">— {opt.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk result/error banners */}
      {bulkResult && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <Check size={15} className="shrink-0 text-emerald-600" />
          <p className="text-small text-emerald-700 flex-1">
            Bulk upload complete — {bulkResult.created} added, {bulkResult.updated} updated
          </p>
          <button type="button" onClick={() => setBulkResult(null)} className="text-emerald-600 hover:text-emerald-800">
            <X size={14} />
          </button>
        </div>
      )}
      {bulkError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose flex-1">{bulkError}</p>
          <button type="button" onClick={() => setBulkError('')} className="text-rose hover:text-rose/80">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header with Bulk + Edit buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="flex-1 font-semibold text-small text-midnight">
          Room Auto-Add Rules
        </h2>

        {saveSuccess && (
          <div className="flex items-center gap-1 text-[12px] text-emerald-600 font-semibold">
            <Check size={14} /> Saved
          </div>
        )}

        {/* Bulk upload dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setBulkMenuOpen(v => !v)}
            disabled={bulkUploading}
            className="shrink-0 h-11 px-3 rounded-lg border border-stone bg-white
                       flex items-center justify-center gap-1.5
                       text-small font-semibold text-midnight
                       hover:border-plum/40 hover:text-plum active:bg-mist
                       disabled:opacity-50 transition-colors duration-150"
          >
            {bulkUploading ? <LoadingSpinner size="sm" /> : <Upload size={14} />}
            Bulk
            <ChevronDown size={12} />
          </button>
          {bulkMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setBulkMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white border border-stone
                              rounded-xl shadow-brand-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-3 py-2.5 text-left text-[13px] text-midnight
                             hover:bg-mist transition-colors flex items-center gap-2"
                >
                  <Upload size={14} className="text-slate" />
                  Upload JSON File
                </button>
                <button
                  type="button"
                  onClick={downloadExistingData}
                  className="w-full px-3 py-2.5 text-left text-[13px] text-midnight border-t border-stone
                             hover:bg-mist transition-colors flex items-center gap-2"
                >
                  <Download size={14} className="text-slate" />
                  Download Existing Data
                </button>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="w-full px-3 py-2.5 text-left text-[13px] text-midnight border-t border-stone
                             hover:bg-mist transition-colors flex items-center gap-2"
                >
                  <Download size={14} className="text-slate" />
                  Download Template
                </button>
              </div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={startEditing}
            className="flex items-center gap-1.5 h-11 px-4 rounded-lg border border-plum bg-plum text-white
                       text-small font-semibold hover:bg-plum/90 active:bg-plum/80 transition-colors"
          >
            <Pencil size={14} /> Edit Rules
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEditing}
              className="h-11 px-3 rounded-lg border border-stone text-small font-semibold text-slate
                         hover:text-midnight hover:border-midnight/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 h-11 px-4 rounded-lg border border-plum bg-plum text-white
                         text-small font-semibold hover:bg-plum/90 active:bg-plum/80 transition-colors
                         disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Rules'}
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{error}</p>
        </div>
      )}

      <RoomCardList
        configs={merged}
        editing={editing}
        uiOption={uiOption}
        onUpdateRules={updateRoomRules}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Org Auto-Add Rules Tab (Org Admin) — with Restore from Master
// ══════════════════════════════════════════════════════════════════════════════

export function OrgAutoAddRulesTab({
  rooms,
  orgId,
}: {
  rooms: OrgResidentialRoom[];
  orgId: string;
}) {
  const { rules: configs, loading, error, saveRules, syncFromMaster } = useOrgAutoAddRules(orgId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MasterRoomAutoAddConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uiOption, setUiOption] = useState<UIOption>('A');

  // Restore state
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState<{ added: number; updated: number } | null>(null);
  const [restoreError, setRestoreError] = useState('');

  const merged = mergeRoomsAndRules(rooms, editing ? draft : configs);

  function startEditing() {
    setDraft(mergeRoomsAndRules(rooms, configs));
    setEditing(true);
    setSaveError('');
    setSaveSuccess(false);
  }

  function cancelEditing() {
    setEditing(false);
    setDraft([]);
    setSaveError('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      const orgDraft: OrgRoomAutoAddConfig[] = draft
        .filter(d => d.rules.length > 0)
        .map(d => {
          const existing = configs.find(r => r.roomId === d.roomId);
          return {
            ...d,
            source: (existing as OrgRoomAutoAddConfig | undefined)?.source ?? 'org',
            masterRuleId: (existing as OrgRoomAutoAddConfig | undefined)?.masterRuleId,
          };
        });
      await saveRules(orgDraft);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    setRestoring(true);
    setRestoreError('');
    setRestoreResult(null);
    try {
      const result = await syncFromMaster();
      setRestoreResult(result);
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoring(false);
      setShowRestoreConfirm(false);
    }
  }

  function updateRoomRules(roomId: string, rules: RoomAutoAddRule[]) {
    setDraft(prev => prev.map(r => r.roomId === roomId ? { ...r, rules } : r));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 gap-3 text-center">
        <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
          <Home size={24} className="text-slate" />
        </div>
        <p className="text-small text-slate">Add rooms in the Room Inventories tab first.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* UI Option Switcher */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <p className="text-[12px] font-semibold text-amber-800 mb-2">
          Design Review: Choose a UI style to preview
        </p>
        <div className="flex flex-col gap-1.5">
          {UI_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setUiOption(opt.key)}
              className={`text-left px-3 py-2 rounded-lg border transition-all text-[12px]
                ${uiOption === opt.key
                  ? 'border-amber-400 bg-amber-100 text-amber-900 font-semibold'
                  : 'border-amber-200 bg-white text-amber-800 hover:border-amber-300'
                }`}
            >
              <span className="font-semibold">{opt.label}</span>
              <span className="text-amber-600 ml-1">— {opt.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Restore banners */}
      {restoreResult && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
          <div className="flex-1 min-w-0">
            <p className="text-small font-semibold text-midnight">
              Restore complete — {restoreResult.added > 0 ? `${restoreResult.added} added` : ''}{restoreResult.added > 0 && restoreResult.updated > 0 ? ', ' : ''}{restoreResult.updated > 0 ? `${restoreResult.updated} updated` : ''}{restoreResult.added === 0 && restoreResult.updated === 0 ? 'no changes' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRestoreResult(null)}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-slate hover:text-midnight transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {restoreError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose flex-1">{restoreError}</p>
          <button type="button" onClick={() => setRestoreError('')} className="text-rose hover:text-rose/80">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header with Restore + Edit buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="flex-1 font-semibold text-small text-midnight">
          Room Auto-Add Rules
        </h2>

        {saveSuccess && (
          <div className="flex items-center gap-1 text-[12px] text-emerald-600 font-semibold">
            <Check size={14} /> Saved
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowRestoreConfirm(true)}
          disabled={restoring}
          className="shrink-0 h-11 px-3 rounded-lg border border-stone bg-white
                     flex items-center gap-1.5
                     text-small font-semibold text-slate
                     hover:border-plum/40 hover:text-plum active:bg-mist
                     disabled:opacity-50 transition-colors duration-150"
        >
          <RefreshCw size={14} className={restoring ? 'animate-spin' : ''} />
          Restore
        </button>

        {!editing ? (
          <button
            type="button"
            onClick={startEditing}
            className="flex items-center gap-1.5 h-11 px-4 rounded-lg border border-plum bg-plum text-white
                       text-small font-semibold hover:bg-plum/90 active:bg-plum/80 transition-colors"
          >
            <Pencil size={14} /> Edit Rules
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEditing}
              className="h-11 px-3 rounded-lg border border-stone text-small font-semibold text-slate
                         hover:text-midnight hover:border-midnight/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 h-11 px-4 rounded-lg border border-plum bg-plum text-white
                         text-small font-semibold hover:bg-plum/90 active:bg-plum/80 transition-colors
                         disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Rules'}
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{error}</p>
        </div>
      )}

      <RoomCardList
        configs={merged}
        editing={editing}
        uiOption={uiOption}
        onUpdateRules={updateRoomRules}
      />

      {/* Restore confirm modal */}
      <ConfirmModal
        isOpen={showRestoreConfirm}
        title="Restore from Master"
        message="This will sync your auto-add rules with the master defaults. New rules will be added and existing system rules will be updated. Your custom rules will not be affected."
        confirmLabel="Restore"
        variant="primary"
        onConfirm={handleRestore}
        onClose={() => setShowRestoreConfirm(false)}
      />
    </div>
  );
}
